import { useEffect, useState } from "react";
import { api, formatarErroApi, API_BASE } from "@/api";
import { LayoutAutenticado } from "@/componentes/LayoutAutenticado";
import { useAutenticacao } from "@/contextos/AutenticacaoContexto";
import { toast } from "sonner";
import { Download } from "lucide-react";

export default function ConfiguracoesSistema() {
  const { usuario } = useAutenticacao();
  const ehSuper = usuario?.perfil === "SUPER_ADMIN";
  const [cfg, setCfg] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const carregarConfig = async () => {
    try {
      const { data } = await api.get("/configuracao");
      console.log("Configuração carregada:", data);
      setCfg(data || {});
    } catch (e) {
      console.error("Erro ao carregar configuração:", e);
    }
  };

  useEffect(() => {
    carregarConfig();
  }, []);

  const salvar = async (e) => {
    e.preventDefault();
    if (!ehSuper) return;
    setEnviando(true);
    try {
      const payload = {
        nomeSistema: cfg.nomeSistema,
        horasBloqueioAntesJogo: Number(cfg.horasBloqueioAntesJogo),
        valorEntrada: cfg.valorEntrada ? Number(cfg.valorEntrada) : null,
        percentualPrimeiroLugar: Number(cfg.percentualPrimeiroLugar),
        percentualSegundoLugar: Number(cfg.percentualSegundoLugar),
        percentualTerceiroLugar: Number(cfg.percentualTerceiroLugar),
        percentualQuartoLugar: Number(cfg.percentualQuartoLugar),
        percentualQuintoLugar: Number(cfg.percentualQuintoLugar),
      };
      console.log("Enviando payload:", payload);
      const { data } = await api.patch("/configuracao", payload);
      console.log("Resposta do servidor:", data);
      toast.success("Configuração salva");
      // Recarregar configuração para confirmar
      await carregarConfig();
    } catch (e) { 
      console.error("Erro ao salvar:", e);
      toast.error(formatarErroApi(e)); 
    }
    finally { setEnviando(false); }
  };

  if (!cfg) return <LayoutAutenticado><div className="text-white/40">Carregando...</div></LayoutAutenticado>;

  const baixarRanking = () => {
    const token = localStorage.getItem("bolao_token");
    fetch(`${API_BASE}/exportacao/ranking.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "ranking_bolao.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const baixarPalpites = () => {
    const token = localStorage.getItem("bolao_token");
    fetch(`${API_BASE}/exportacao/palpites.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "palpites_bolao.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <LayoutAutenticado>
      <div data-testid="pagina-configuracoes">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-amarelobrasil">Sistema</div>
          <h1 className="font-heading text-4xl font-bold uppercase mt-1">Configurações</h1>
          {!ehSuper && <p className="text-sm text-white/60 mt-1">Apenas o Super Admin pode alterar estes valores.</p>}
        </div>

        <form onSubmit={salvar} className="grid md:grid-cols-2 gap-4 max-w-3xl">
          <Campo label="Nome do sistema" valor={cfg.nomeSistema} setValor={(v) => setCfg({ ...cfg, nomeSistema: v })} disabled={!ehSuper} testId="cfg-nome" />
          <div>
            <Campo label="Horas de bloqueio antes do jogo" valor={cfg.horasBloqueioAntesJogo} setValor={(v) => setCfg({ ...cfg, horasBloqueioAntesJogo: v })} type="number" disabled={!ehSuper} testId="cfg-horas" />
            <p className="text-[10px] text-white/40 mt-1">Este valor define o tempo de bloqueio antes do jogo começar.</p>
          </div>
          <div>
            <Campo label="Valor de Inscrição (R$)" valor={cfg.valorEntrada || ""} setValor={(v) => setCfg({ ...cfg, valorEntrada: v })} type="number" step="0.01" disabled={!ehSuper} testId="cfg-valor" />
            <p className="text-[10px] text-white/40 mt-1"></p>
          </div>
          <Campo label="% 1º lugar" valor={cfg.percentualPrimeiroLugar} setValor={(v) => setCfg({ ...cfg, percentualPrimeiroLugar: v })} type="number" disabled={!ehSuper} testId="cfg-p1" />
          <Campo label="% 2º lugar" valor={cfg.percentualSegundoLugar} setValor={(v) => setCfg({ ...cfg, percentualSegundoLugar: v })} type="number" disabled={!ehSuper} testId="cfg-p2" />
          <Campo label="% 3º lugar" valor={cfg.percentualTerceiroLugar} setValor={(v) => setCfg({ ...cfg, percentualTerceiroLugar: v })} type="number" disabled={!ehSuper} testId="cfg-p3" />
          <Campo label="% 4º lugar" valor={cfg.percentualQuartoLugar} setValor={(v) => setCfg({ ...cfg, percentualQuartoLugar: v })} type="number" disabled={!ehSuper} testId="cfg-p4" />
          <Campo label="% 5º lugar" valor={cfg.percentualQuintoLugar} setValor={(v) => setCfg({ ...cfg, percentualQuintoLugar: v })} type="number" disabled={!ehSuper} testId="cfg-p5" />

          {ehSuper && (
            <button
              type="submit"
              disabled={enviando}
              className="md:col-span-2 py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark disabled:opacity-50"
              data-testid="btn-salvar-config"
            >
              {enviando ? "Salvando..." : "Salvar Configurações"}
            </button>
          )}
        </form>

        <div className="mt-12">
          <h2 className="font-heading uppercase font-bold text-2xl mb-4">Exportações</h2>
          <div className="flex flex-wrap gap-3">
            <button onClick={baixarRanking} className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest border border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base flex items-center gap-2" data-testid="btn-exportar-ranking">
              <Download className="w-4 h-4" /> Ranking (CSV)
            </button>
            <button onClick={baixarPalpites} className="px-4 py-2.5 rounded-md text-xs font-heading uppercase tracking-widest border border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base flex items-center gap-2" data-testid="btn-exportar-palpites">
              <Download className="w-4 h-4" /> Palpites (CSV)
            </button>
          </div>
        </div>
      </div>
    </LayoutAutenticado>
  );
}

function Campo({ label, valor, setValor, type = "text", step, disabled, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold">{label}</label>
      <input
        type={type}
        step={step}
        value={valor ?? ""}
        onChange={(e) => setValor(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full bg-superficie-base border border-white/10 rounded-lg px-4 py-3 text-sm disabled:opacity-50"
        data-testid={testId}
      />
    </div>
  );
}
